import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  Snackbar,
  Link as MuiLink
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getUniqueCustomers } from '../services/customerEngagementService';
import { AggregatedStats, UniqueCustomerData } from '../types/customer';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F8F9FA'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 6),
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%'
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4)
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  border: '1px solid #EAECEF',
  borderRadius: theme.spacing(1.5),
  boxShadow: 'none'
}));

const TableCard = styled(Card)(({ theme }) => ({
  border: '1px solid #EAECEF',
  borderRadius: theme.spacing(1.5),
  boxShadow: 'none',
  padding: theme.spacing(3)
}));

const Customers = () => {
  const userData = getFullUserData();
  const businessName = getUserBusinessName();

  const [stats, setStats] = useState<AggregatedStats | null>(null);
  const [customers, setCustomers] = useState<UniqueCustomerData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchCustomerData();
  }, [currentPage, searchQuery]);

  const fetchCustomerData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * limit;
      const response = await getUniqueCustomers(limit, offset, searchQuery);

      if (response.success && response.data) {
        setStats(response.data.aggregated_stats);
        // Handle null customers array from backend - default to empty array
        setCustomers(response.data.customers || []);
        setTotalRecords(response.data.total_customers || 0);
        setTotalPages(Math.ceil((response.data.total_customers || 0) / limit));
      } else {
        throw new Error(response.message || 'Failed to fetch customer engagement data');
      }
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customer data');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleExportData = () => {
    // TODO: Implement export functionality
    console.log('Exporting customer engagement data...');
  };

  const formatPercentChange = (value: number) => {
    const isPositive = value >= 0;
    const color = isPositive ? '#10B981' : '#EF4444';
    const sign = isPositive ? '+' : '';
    return (
      <Typography
        variant="body2"
        sx={{ color, fontWeight: 500, fontSize: '0.875rem' }}
      >
        {sign}{value}% from last month
      </Typography>
    );
  };

  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, totalRecords);

  return (
    <PageContainer>
      <Sidebar user={userData} />
      <MainContent>
        <DashboardHeader 
          businessName={businessName} 
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        <ContentArea>
          {/* Page Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}
            >
              Customer Engagement
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor and analyze customer engagement with your campaigns.
            </Typography>
          </Box>

          {/* Stats Cards */}
          {stats && (
            <StatsContainer>
              <StatCard>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Total Customers
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: '2rem'
                  }}
                >
                  {stats.total_customers.toLocaleString()}
                </Typography>
                {formatPercentChange(stats.total_customers_change)}
              </StatCard>

              <StatCard>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Overall Response Rate
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: '2rem'
                  }}
                >
                  {stats.overall_response_rate}%
                </Typography>
                {formatPercentChange(stats.response_rate_change)}
              </StatCard>

              <StatCard>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Total Rewards Claimed
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: '2rem'
                  }}
                >
                  {stats.total_rewards_claimed.toLocaleString()}
                </Typography>
                {formatPercentChange(stats.rewards_claimed_change)}
              </StatCard>
            </StatsContainer>
          )}

          {/* Table Section */}
          <TableCard>
            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3,
                justifyContent: 'space-between'
              }}
            >
              <TextField
                placeholder="Search by customer email or phone..."
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{
                  flex: 1,
                  maxWidth: { sm: '400px' },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F8F9FA'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  sx={{
                    backgroundColor: '#F8F9FA',
                    borderColor: '#E5E7EB',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      borderColor: '#D1D5DB'
                    }
                  }}
                >
                  Filter by Status
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  sx={{
                    backgroundColor: '#F8F9FA',
                    borderColor: '#E5E7EB',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      borderColor: '#D1D5DB'
                    }
                  }}
                >
                  Date Range
                </Button>

                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportData}
                  sx={{
                    backgroundColor: '#4A90E2',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#3A7BC8'
                    }
                  }}
                >
                  Export Data
                </Button>
              </Box>
            </Box>

            {/* Table */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Customer
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Surveys Sent
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Surveys Opened
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Surveys Responded
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Rewards Claimed
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customers && customers.length > 0 ? (
                        customers.map((customer, index) => {
                        const openedPercent = customer.total_surveys_sent > 0 
                          ? Math.round((customer.surveys_opened / customer.total_surveys_sent) * 100)
                          : 0;
                        const completedPercent = customer.total_surveys_sent > 0
                          ? Math.round((customer.surveys_completed / customer.total_surveys_sent) * 100)
                          : 0;

                        return (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontWeight: 500 }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {customer.customer_name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {customer.customer_email || customer.customer_phone}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {customer.total_surveys_sent}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {customer.surveys_opened} ({openedPercent}%)
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {customer.surveys_completed} ({completedPercent}%)
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {customer.rewards_claimed}
                            </TableCell>
                            <TableCell align="right">
                              <MuiLink
                                href="#"
                                sx={{
                                  color: '#4A90E2',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                View Details
                              </MuiLink>
                            </TableCell>
                          </TableRow>
                        );
                      })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                No customers found
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {searchQuery 
                                  ? 'Try adjusting your search criteria'
                                  : 'Start creating campaigns to see customer engagement data here'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #E5E7EB',
                    gap: 2
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing <Box component="span" sx={{ fontWeight: 600 }}>{startRecord}</Box> to{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>{endRecord}</Box> of{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>{totalRecords}</Box> results
                  </Typography>

                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </>
            )}
          </TableCard>
        </ContentArea>
      </MainContent>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Customers;

